import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

// Items without a `group` render together under no header (matches
// departments like Infosec/H&S that only have one or two ungrouped items).
function groupItems(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.group || null;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return groups;
}

function ItemCard({ item }) {
  const ItemIcon = item.icon;
  return (
    <a
      href={item.url}
      className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
    >
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-light">
        <ItemIcon className="h-5 w-5 text-brand" />
      </div>
      <h2 className="text-base font-semibold text-gray-900">{item.name}</h2>
      <p className="mt-1 flex-1 text-sm text-gray-500">{item.description}</p>
      <span className="mt-3 self-end text-sm font-medium text-brand">Access &rarr;</span>
    </a>
  );
}

// Department access (checked before this component ever renders — see
// filterDepartmentsByAccess) only gets you as far as the department view.
// Within it, an item with a headerKey is only shown if the user is also a
// member of that header's group — mirrors the RequireGroup gate the
// destination app itself enforces, so the nav doesn't show a link that
// would just bounce the user straight back.
// A pageKey card is granted person by person (Platform > Page access) and
// shows only for holders, department member or not. Someone who reaches the
// department only through such a card sees just those cards.
function visibleItems(items, access, inDepartment) {
  return items.filter((item) => {
    // pageOrDepartment (Support, 03/11/2026): the department's group still
    // opens the card; the page gives it to anyone else.
    if (item.pageKey) return Boolean(access?.pages?.[item.pageKey]) || Boolean(item.pageOrDepartment && inDepartment);
    if (!inDepartment) return false;
    return !item.headerKey || access?.headers?.includes(item.headerKey);
  });
}

function ModulePanel({ departmentKey, departments, access }) {
  // Collapsed named groups (in-memory, per session). Ungrouped items have no
  // header and are always shown.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const dept = departments.find((d) => d.key === departmentKey);
  if (!dept) return null;

  const DeptIcon = dept.icon;
  const items = visibleItems(dept.items, access, Boolean(access?.departments?.includes(dept.key)));
  const groups = groupItems(items);

  const toggle = (name) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center gap-3">
        <DeptIcon className="h-9 w-9 text-brand" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{dept.fullName || dept.name}</h1>
          <p className="text-sm text-gray-500">{dept.description}</p>
        </div>
      </div>

      <div className="space-y-8">
        {Array.from(groups.entries()).map(([groupName, groupItemsList]) => {
          const isCollapsed = groupName ? collapsed.has(groupName) : false;
          return (
            <div key={groupName ?? '__ungrouped'}>
              {groupName && (
                <button
                  type="button"
                  onClick={() => toggle(groupName)}
                  aria-expanded={!isCollapsed}
                  className="mb-3 flex w-full items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                  />
                  {groupName}
                  <span className="ml-1 normal-case tracking-normal text-gray-400">({groupItemsList.length})</span>
                </button>
              )}
              {!isCollapsed && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {groupItemsList.map((item) => (
                    <ItemCard key={item.name} item={item} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-gray-400">Nothing here yet.</p>}
      </div>
    </div>
  );
}

export default ModulePanel;
